import "@fontsource/roboto";
import "@fontsource/roboto/700.css";
import "@fontsource/space-mono";
import { Auth0Provider } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { DesignsContext } from "@fluidattacks/design";
import * as React from "react";
import { Suspense, useEffect, useMemo, useState } from "react";
import type { ComponentType } from "react";
import { createRoot } from "react-dom/client";
import {
  Route,
  RouterProvider,
  Routes,
  createBrowserRouter,
} from "react-router";

import { GlobalStyle } from "./styles";

import { AppTitle } from "components/app-title";
import { ToastBox } from "components/toast-box";
import type { IAuthContext } from "context/auth";
import { authContext } from "context/auth";
import { PermissionsProvider } from "context/authz/permissions-provider";
import type { IFeaturePreviewContext } from "context/feature-preview";
import { featurePreviewContext } from "context/feature-preview";
import type { IMeetingModeContext } from "context/meeting-mode";
import { meetingModeContext } from "context/meeting-mode";
import { Auth0Callback } from "pages/auth0-callback";
import { CliOauth } from "pages/cli-oauth";
import { CliOauthCallback } from "pages/cli-oauth-callback";
import { CliOauthDone } from "pages/cli-oauth-done";
import { CliOauthError } from "pages/cli-oauth-error";
import { Login } from "pages/login";
import { SignUp } from "pages/sign-up";
import { useStoredState } from "storage/state";
import { ApolloProvider } from "utils/apollo";
import {
  RouteErrorBoundary,
  handleVitePreloadError,
  lazyWithReload,
} from "utils/asset-reload";
import { auth0Config } from "utils/auth0-config";
import { getEnvironment } from "utils/environment";
import {
  CustomErrorBoundary,
  CustomFallback,
  initLogging,
} from "utils/error-boundary";
import { secureStore, secureStoreContext } from "utils/secure-store";
// eslint-disable-next-line import/no-unresolved
import "vite/modulepreload-polyfill";
import "react-toastify/dist/ReactToastify.min.css";
import "tachyons/css/tachyons.min.css";
import "tachyons-word-break/css/tachyons-word-break.min.css";

const LazyHome = lazyWithReload(
  async (): Promise<{ default: ComponentType }> => import("pages/home"),
);

const Root = (): JSX.Element => {
  const [user, setUser] = useState({
    tours: {
      newGroup: true,
      newRoot: true,
      welcome: true,
    },
    userEmail: "",
    userName: "",
  } as IAuthContext);
  const [featurePreview, setFeaturePreview] = useStoredState(
    "featurePreview",
    false,
    "local",
  );
  const [meetingMode, setMeetingMode] = useStoredState(
    "meetingMode",
    false,
    "local",
  );

  const valueAuth = useMemo((): IAuthContext => ({ ...user, setUser }), [user]);

  const valueFeature = useMemo(
    (): IFeaturePreviewContext => ({ featurePreview, setFeaturePreview }),
    [featurePreview, setFeaturePreview],
  );

  const valueMeetingMode = useMemo(
    (): IMeetingModeContext => ({ meetingMode, setMeetingMode }),
    [meetingMode, setMeetingMode],
  );

  return (
    <Auth0Provider
      authorizationParams={auth0Config.authorizationParams}
      clientId={auth0Config.clientId}
      domain={auth0Config.domain}
    >
      <ApolloProvider>
        <PermissionsProvider>
          <secureStoreContext.Provider value={secureStore}>
            <authContext.Provider value={valueAuth}>
              <featurePreviewContext.Provider value={valueFeature}>
                <meetingModeContext.Provider value={valueMeetingMode}>
                  <AppTitle />
                  <Routes>
                    <Route element={<Login />} path={"/"} />
                    <Route element={<SignUp />} path={"/SignUp"} />
                    <Route
                      element={<Auth0Callback />}
                      path={"/auth/auth0/callback"}
                    />
                    <Route element={<CliOauth />} path={"/auth/cli"} />
                    <Route element={<CliOauthDone />} path={"/auth/cli/done"} />
                    <Route
                      element={<CliOauthError />}
                      path={"/auth/cli/error"}
                    />
                    <Route
                      element={<CliOauthCallback />}
                      path={"/auth/oauth/callback"}
                    />
                    <Route
                      element={
                        <Suspense fallback={<div />}>
                          <LazyHome />
                        </Suspense>
                      }
                      path={"/*"}
                    />
                  </Routes>
                </meetingModeContext.Provider>
              </featurePreviewContext.Provider>
            </authContext.Provider>
          </secureStoreContext.Provider>
        </PermissionsProvider>
      </ApolloProvider>
    </Auth0Provider>
  );
};

const router = createBrowserRouter([
  { element: <Root />, errorElement: <RouteErrorBoundary />, path: "*" },
]);

initLogging();

window.addEventListener("vite:preloadError", handleVitePreloadError);

const App: React.FC = (): JSX.Element => {
  // eslint-disable-next-line functional/immutable-data
  window.global = window;

  useEffect((): void => {
    if (getEnvironment() === "production") {
      datadogRum.startSessionReplayRecording();
    }
  }, []);

  return (
    <React.StrictMode>
      <GlobalStyle />
      <CustomErrorBoundary fallback={CustomFallback}>
        <DesignsContext>
          <RouterProvider router={router} />
        </DesignsContext>
        <ToastBox />
      </CustomErrorBoundary>
    </React.StrictMode>
  );
};

const rootElement = document.getElementById("root");
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(<App />);
}
